<template>
  <Card class="w-full">
    <template #title>Flex Row Dates</template>
    <template #content>
      <div class="flex flex-col w-full h-[70vh]">
        <ag-grid-vue
          theme="legacy"
          :rowData="fields"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          class="mx-4 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          :suppressDragLeaveHidesColumns="true"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { FlexRowFieldListSchema, type FlexRowField } from "@/types/FlexRows";
import { CustomFieldScope } from "@/enums/CustomFieldScope";
import CustomTagRenderer from "../cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import CustomCheckboxRenderer from "../cell-renderers/CustomCheckboxRenderer.vue";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, ValueGetterParams } from "ag-grid-community";

//TODO: Imbued with newfound knowledge, I really need to go around and change all tables to the immutable data method and fix all the missing type safety

const gridApi = ref<GridApi>();

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const fields = ref<FlexRowField[]>([]);

const updateStopPayments = (index: number, checked: boolean): void => {
  const dataRow = gridApi.value?.getDisplayedRowAtIndex(index);
  if (dataRow == null) return;
  dataRow.setDataValue("stopPayments", checked);
  updateField(dataRow, "stopPayments");
};

const updateDashboardDisplay = (index: number, checked: boolean): void => {
  const dataRow = gridApi.value?.getDisplayedRowAtIndex(index);
  if (dataRow == null) return;
  dataRow.setDataValue("dashboardDisplay", checked);
  updateField(dataRow, "dashboardDisplay");
};

const updateField = (dataRow: any, field: string): void => {
  axios
    .post("/flex-rows/fields/update-options", dataRow.data)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Flex row options successfully updated");
        },
        (errors: ApiError[]) => {
          error("An error occurred updating flex row options", errors[0].message);
          if (field === "stopPayments") {
            dataRow.setDataValue(field, !dataRow.data.stopPayments);
          } else if (field === "dashboardDisplay") {
            dataRow.setDataValue(field, !dataRow.data.dashboardDisplay);
          }
        }
      )
    )
    .catch((e) => errorApi(e));
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  cellDataType: "text",
  editable: false,
  wrapHeaderText: true,
};

const colDefs = ref([
  {
    field: "stopPayments",
    headerName: "Stop Payments",
    cellDataType: "boolean",
    headerClass: "grid-align-center text-center",
    cellClass: "text-center",
    maxWidth: 90,
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: () => {
      return {
        setValue: updateStopPayments,
        disabled: false,
      };
    },
  },
  {
    field: "dashboardDisplay",
    headerName: "Dashboard Display",
    cellDataType: "boolean",
    headerClass: "grid-align-center text-center",
    cellClass: "text-center",
    maxWidth: 90,
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: () => {
      return {
        setValue: updateDashboardDisplay,
        disabled: false,
      };
    },
  },
  {
    field: "scope",
    headerName: "Scope",
    cellDataType: "text",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    valueGetter: (params: ValueGetterParams) => CustomFieldScope[params.data.scope],
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.CUSTOM_FIELD_SCOPE,
    },
    maxWidth: 150,
  },
  {
    field: "label",
    headerName: "Label",
    width: 100,
  },
]);

onBeforeMount(async (): Promise<void> => {
  await axios
    .get("/flex-rows/fields/date")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(FlexRowFieldListSchema, data, fields);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
});
</script>
